import { queryOptions, useQuery } from '@tanstack/react-query';

import { getApiClient } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import type { ReportingMonetizationPricesResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { REPORTING_TRIAL_ENABLED } from './reportingTrialEnabled';

const REPORTING_MONETIZATION_PRICES_QUERY_KEY = 'reporting-monetization-prices';

const getReportingMonetizationPricesQueryKey = (organizationId?: string) =>
  [REPORTING_MONETIZATION_PRICES_QUERY_KEY, organizationId] as const;

const getReportingMonetizationPricesQueryOptions = (
  organizationId: string | undefined,
  enabled: boolean
) =>
  queryOptions({
    queryKey: getReportingMonetizationPricesQueryKey(organizationId),
    queryFn: async (): Promise<ReportingMonetizationPricesResponse> => {
      const { data } =
        await getApiClient().costReport.getReportingMonetizationPrices();
      return data;
    },
    enabled: makeIsEnabled(enabled, !!organizationId, REPORTING_TRIAL_ENABLED),
    staleTime: 5 * 60 * 1000,
  });

export const useReportingMonetizationPricesQuery = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { isReady: isOrganizationReady, currentOrganization } =
    useOrganizations();

  return useQuery(
    getReportingMonetizationPricesQueryOptions(
      currentOrganization?.id,
      enabled && isOrganizationReady
    )
  );
};
